import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import addMediaElementOperation from 'editor/src/store/design/operation/addMediaElementOperation';
import removeMediaElementOperation from 'editor/src/store/design/operation/removeMediaElementOperation';
import { getStructureIndexByElementUuid } from 'editor/src/store/design/selector/getStructureIndexByElementUuid';
import { MediaImage } from 'editor/src/store/design/types';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import { DEFAULT_MAX_TOTAL_COPIES } from 'editor/src/store/gangSheet/constants';
import getArtworkGroups from 'editor/src/store/gangSheet/util/getArtworkGroups';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import { elementUuids } from 'editor/src/util/uuids';

// mm; matches the shift copyMediaElementOperation applies to a duplicate
const COPY_SHIFT_MM = 10;

interface SetArtworkQtyOptions {
  // false when a caller (e.g. fillSheetWithArtworkOperation) already saved its own undo step
  saveUndoRedoStep?: boolean;
}

// Sets the number of canvas copies of one artwork group (one artwork at one size).
// The canvas is the source of truth: raising the quantity clones the group's last
// element (cascade-shifted, like a manual duplicate), lowering it removes the group's
// last elements. Setting 0 removes the artwork from the sheet. The buyer re-packs
// with Auto nest — this operation never moves existing elements.
const setArtworkQtyOperation =
  (groupKey: string, qty: number, { saveUndoRedoStep = true }: SetArtworkQtyOptions = {}) =>
  (dispatch: ThunkDispatch, getState: () => RootState): void => {
    const state = getState();
    const gangSheetSettings = state.hostSettings.gangSheet;
    if (!gangSheetSettings) {
      return;
    }

    const spreadIndex = getCurrentSpreadIndex(state);
    const groups = getArtworkGroups(state.design.designData?.spreads[spreadIndex]);
    const group = groups.find((candidate) => candidate.key === groupKey);
    if (!group) {
      return;
    }

    const maxTotalCopies = gangSheetSettings.maxTotalCopies ?? DEFAULT_MAX_TOTAL_COPIES;
    const totalCopies = groups.reduce((sum, candidate) => sum + candidate.elements.length, 0);
    const otherCopies = totalCopies - group.elements.length;
    const targetQty = Math.max(0, Math.min(qty, maxTotalCopies - otherCopies));
    const currentQty = group.elements.length;
    if (targetQty === currentQty) {
      return;
    }

    batch(() => {
      if (saveUndoRedoStep) {
        dispatch(saveUndoRedoStateOperation('Change copies'));
      }

      if (targetQty > currentQty) {
        const source = group.elements[group.elements.length - 1];
        for (let i = 1; i <= targetQty - currentQty; i += 1) {
          const copy: MediaImage = {
            ...source,
            uuid: elementUuids.generate(),
            x: source.x + COPY_SHIFT_MM * i,
            y: source.y + COPY_SHIFT_MM * i,
          };
          dispatch(addMediaElementOperation(spreadIndex, 0, copy, false));
        }
      } else {
        group.elements.slice(targetQty).forEach((element) => {
          const elementAddress = getStructureIndexByElementUuid(getState().design.designData, element.uuid);
          if (elementAddress) {
            dispatch(removeMediaElementOperation(elementAddress, false));
          }
        });
      }
    });
  };

export default setArtworkQtyOperation;
